import {
  addCalendarDays,
  addCalendarMonths,
  type CalendarDate,
  fromCalendarDate,
  toCalendarDate,
  toDate,
} from "@repo/utils/date";

const DAY_MS = 86_400_000;

/** Most urgent first. Missed stages are not replayed. */
export const DOCUMENT_EXPIRY_STEPS = [
  { event: "EXPIRED_PAUSE", days: 0 },
  { event: "EXPIRY_REMINDER_SMS_J7", days: 7 },
  { event: "EXPIRY_REMINDER_EMAIL_J14", days: 14 },
  { event: "EXPIRY_REMINDER_PUSH_J30", days: 30 },
] as const;
/** Both scanning and renewal follow the first reminder. Change the schedule once. */
export const DOCUMENT_EXPIRY_HORIZON_DAYS = Math.max(
  ...DOCUMENT_EXPIRY_STEPS.map((step) => step.days)
);
export const DOCUMENT_RENEWAL_WINDOW_DAYS = DOCUMENT_EXPIRY_HORIZON_DAYS;
export type DocumentExpiryEvent = (typeof DOCUMENT_EXPIRY_STEPS)[number]["event"];
export type DocumentValidityState =
  | "VALID"
  | "ABOUT_TO_EXPIRE"
  | "EXPIRED"
  | "MISSING_DATE"
  | "NON_EXPIRING"
  | "UNKNOWN_RULE";

export function dueDocumentExpiryEvent(expiresAt: Date, now: Date): DocumentExpiryEvent | null {
  const remaining = expiresAt.getTime() - now.getTime();
  if (!Number.isFinite(remaining)) return null;
  const days = Math.ceil(remaining / DAY_MS);
  return DOCUMENT_EXPIRY_STEPS.find((step) => days <= step.days)?.event ?? null;
}

export function daysUntil(expiresAt: Date, now: Date): number {
  return Math.ceil((expiresAt.getTime() - now.getTime()) / DAY_MS);
}

/** Pure decision engine. The caller supplies time and performs all effects. */
export function evaluateDocumentValidity(
  name: string,
  expiresAt: Date | string | null | undefined,
  now: Date
) {
  const rule = getDocumentValidityEntry(name);
  const expiry = expiresAt == null ? null : new Date(expiresAt);
  const hasDate = expiry !== null && Number.isFinite(expiry.getTime());
  const state: DocumentValidityState = hasDate
    ? getDocumentValidityState(expiry, now)
    : !rule
      ? "UNKNOWN_RULE"
      : hasComputableValidity(rule)
        ? "MISSING_DATE"
        : "NON_EXPIRING";
  return {
    state,
    daysRemaining: hasDate ? daysUntil(expiry, now) : null,
    renewalOpen: hasDate && isDocumentRenewalAllowed(expiry, now),
    dueEvent: hasDate ? dueDocumentExpiryEvent(expiry, now) : null,
    blocksWork: state === "EXPIRED",
  };
}

export function shouldPauseForDocuments(
  documents: ReadonlyArray<{ name: string; expiresAt: Date | string | null }>,
  now: Date
): boolean {
  return documents.some(
    (document) => evaluateDocumentValidity(document.name, document.expiresAt, now).blocksWork
  );
}

export function documentValidityLabelFr(
  name: string,
  expiresAt: Date | string | null | undefined,
  now: Date
): string {
  const result = evaluateDocumentValidity(name, expiresAt, now);
  switch (result.state) {
    case "NON_EXPIRING":
      return "Sans expiration";
    case "MISSING_DATE":
      return "Date à vérifier";
    case "UNKNOWN_RULE":
      return "Règle de validité non définie";
    case "EXPIRED":
      return result.daysRemaining === 0
        ? "Expiré aujourd’hui"
        : `Expiré depuis ${Math.abs(result.daysRemaining ?? 0)} jour(s)`;
    default:
      return `Expire dans ${result.daysRemaining} jour(s)`;
  }
}

export type DocumentValidityUnit = "DAY" | "MONTH" | "YEAR";

export interface DocumentValidityEntry {
  name: string;
  validity: number | null;
  unit: DocumentValidityUnit | null;
  /** Driver table field that holds the date for this document (null = no date field). */
  driverField?: string;
  /** "issuedAt" = expiresAt = driverDate + validity; "expiryDate" = expiresAt = driverDate directly. */
  dateType?: "issuedAt" | "expiryDate";
}

/**
 * Normalizes document name variants by lowercasing and stripping trailing "Path" / "_path".
 * Used once when assigning uploads to a logical document.
 */
export const normalizeDocName = (name: string): string => name.toLowerCase().replace(/_?path$/, "");

/** Project policy, not inferred from form widgets. Date-less rules are explicitly
 * non-expiring. Keep aliases here while legacy upload names remain in use.
 * Issue-date rules use calendar months/years; expiry-date rules use the printed date.
 */
export const DOCUMENT_VALIDITY: readonly DocumentValidityEntry[] = [
  // CNI — no date picker in mobile form → non-expiring
  { name: "idPath", validity: null, unit: null },
  { name: "idPath2", validity: null, unit: null },

  // Permis de conduire (plastic) — 15 years; drivingPermitDueDate date
  // field exists in the form and is used directly as the expiry date
  {
    name: "drivingLicensePath",
    validity: 15,
    unit: "YEAR",
    driverField: "drivingPermitDueDate",
    dateType: "expiryDate",
  },
  {
    name: "drivingLicensePath2",
    validity: 15,
    unit: "YEAR",
    driverField: "drivingPermitDueDate",
    dateType: "expiryDate",
  },

  // Solde de permis — no date picker in mobile form → non-expiring
  { name: "permit_solde_path", validity: null, unit: null },

  // Attestation d'assurance — 1 year; insurance_expiration_date date
  // field exists in the form and is used directly as the expiry date
  {
    name: "insurance_document_path",
    validity: 1,
    unit: "YEAR",
    driverField: "insurance_expiration_date",
    dateType: "expiryDate",
  },

  // Kbis — 3 months; kbis_issue_date issue-date field exists in the form
  {
    name: "kbisPath",
    validity: 3,
    unit: "MONTH",
    driverField: "kbis_issue_date",
    dateType: "issuedAt",
  },
  {
    name: "kbis",
    validity: 3,
    unit: "MONTH",
    driverField: "kbis_issue_date",
    dateType: "issuedAt",
  },

  // Attestation de vigilance URSSAF — 6 months; vigilance_issue_date
  // issue-date field exists in the form
  {
    name: "vigilance_certificate_path",
    validity: 6,
    unit: "MONTH",
    driverField: "vigilance_issue_date",
    dateType: "issuedAt",
  },
  {
    name: "vigilance_certificate",
    validity: 6,
    unit: "MONTH",
    driverField: "vigilance_issue_date",
    dateType: "issuedAt",
  },

  // RIB — no date picker in mobile form → non-expiring
  { name: "ribPath", validity: null, unit: null },
];

/** All known document names (the source of truth for name-based lists). */
export const ALL_DOCUMENT_NAMES = DOCUMENT_VALIDITY.map((e) => e.name);

/** All document names that require admin validation (all valid document names). */
export const NEED_ADMIN_VALIDATION_NAMES = ALL_DOCUMENT_NAMES;
const _validityMap = new Map(DOCUMENT_VALIDITY.map((entry) => [entry.name, entry]));

/**
 * Look up the validity configuration for a document name.
 * Returns the entry if found, undefined otherwise.
 */
export function getDocumentValidityEntry(name: string): DocumentValidityEntry | undefined {
  return (
    _validityMap.get(name) ??
    DOCUMENT_VALIDITY.find((entry) => normalizeDocName(entry.name) === normalizeDocName(name))
  );
}

/**
 * Returns true when the document has a real, computable validity period.
 * Documents whose mobile section is upload-only return false here and
 * keep `expiresAt = null`.
 */
export function hasComputableValidity(
  entry: DocumentValidityEntry | undefined
): entry is DocumentValidityEntry & { validity: number; unit: DocumentValidityUnit } {
  return !!entry && typeof entry.validity === "number" && !!entry.unit;
}

/**
 * Compute an expiresAt date from a document validity entry and a base date.
 * Returns null if the entry has no computable validity (non-expiring doc).
 */
export function computeExpiresAt(entry: DocumentValidityEntry, baseDate: Date): Date | null {
  if (!hasComputableValidity(entry) || !toDate(baseDate)) return null;
  return fromCalendarDate(addDocumentValidity(entry, toCalendarDate(baseDate)));
}

/** Month ends clamp: 31 January + one month is February's last day. */
export function addDocumentValidity(
  entry: DocumentValidityEntry & { validity: number; unit: DocumentValidityUnit },
  date: CalendarDate
): CalendarDate {
  if (entry.unit === "DAY") return addCalendarDays(date, entry.validity);
  return addCalendarMonths(date, entry.validity * (entry.unit === "YEAR" ? 12 : 1));
}

/** Renewal is a decision at request time, including documents already overdue. */
export function isDocumentRenewalAllowed(
  expiresAt: Date | string | null | undefined,
  now: Date
): boolean {
  if (expiresAt == null) return false;
  const expiry = new Date(expiresAt).getTime();
  return (
    Number.isFinite(expiry) && expiry <= now.getTime() + DOCUMENT_RENEWAL_WINDOW_DAYS * 86_400_000
  );
}

/** Snapshot a document's real date; unknown dates remain unknown. */
export function getDocumentExpiryFromDates(
  name: string,
  dates: Record<string, Date | string | null | undefined>
): Date | null {
  const entry = getDocumentValidityEntry(name);
  if (!entry?.driverField) return null;
  const raw = dates[entry.driverField];
  if (raw == null) return null;
  const date = new Date(raw);
  if (!Number.isFinite(date.getTime())) return null;
  return entry.dateType === "expiryDate" ? date : computeExpiresAt(entry, date);
}

export function getDocumentValidityState(
  expiresAt: Date | string | null | undefined,
  now: Date
): "VALID" | "EXPIRED" | "ABOUT_TO_EXPIRE" {
  if (expiresAt == null) return "VALID";
  const expiry = new Date(expiresAt).getTime();
  if (expiry <= now.getTime()) return "EXPIRED";
  return isDocumentRenewalAllowed(expiresAt, now) ? "ABOUT_TO_EXPIRE" : "VALID";
}
